import { Fragment, useEffect, useState } from "react";
import { AlertTriangle, CreditCard, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

/** Shown wherever a paid trip's Total has grown past what was collected.
 *  Compact strip: original paid · new total · balance due + "Charge Balance". */
export function BalanceDueBanner({ booking, onChanged, compact = false }) {
  const [open, setOpen] = useState(false);
  const isDue = booking.payment_status === "balance_due" && (booking.balance_due_cents || 0) > 0;
  const supp = booking.supplemental_charges || [];
  if (!isDue && supp.length === 0) return null;

  const totalCents = Math.round((booking.fare || 0) * 100);
  const paidCents = booking.paid_toward_fare_cents ?? totalCents;
  const origPaid = booking.original_paid_cents ?? (paidCents - supp.reduce((a, s) => a + (s.amount_cents || 0), 0));

  return (
    <div data-testid="balance-due-banner" className={`mt-3 border ${isDue ? "border-amber-500/50 bg-amber-500/10" : "border-emerald-500/30 bg-emerald-500/5"} ${compact ? "p-2.5" : "p-3"}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="text-xs space-y-1">
          <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] ${isDue ? "text-amber-300" : "text-emerald-300"}`}>
            {isDue ? <AlertTriangle size={12}/> : <CheckCircle2 size={12}/>}
            {isDue ? "Partially paid · Balance due" : "Price modified · Fully collected"}
          </div>
          <div className="grid grid-cols-[auto_auto] gap-x-4 gap-y-0.5 text-[#F5F2EB]">
            <span className="text-[#A1A1AA]">Originally paid</span><span data-testid="bal-original-paid" className="text-right">{money(origPaid)}</span>
            {supp.map((s, i) => (
              <Fragment key={i}>
                <span className="text-[#A1A1AA]">+ {s.reason}{s.card_last4 ? ` · ••••${s.card_last4}` : ""}</span>
                <span data-testid={`bal-supp-${i}`} className="text-right text-emerald-300">{money(s.amount_cents)}</span>
              </Fragment>
            ))}
            <span className="text-[#A1A1AA]">New total</span><span data-testid="bal-new-total" className="text-right">{money(totalCents)}</span>
            {isDue && (<>
              <span className="text-amber-300 font-medium">Balance due</span>
              <span data-testid="bal-due" className="text-right text-amber-300 font-medium">{money(booking.balance_due_cents)}</span>
            </>)}
          </div>
        </div>
        {isDue && (
          <button data-testid="charge-balance-btn" onClick={() => setOpen(true)}
            className="btn-gold !py-1.5 !px-3 text-[10px] inline-flex items-center gap-1.5 whitespace-nowrap">
            <CreditCard size={12}/> Charge Balance {money(booking.balance_due_cents)}
          </button>
        )}
      </div>
      {open && <ChargeBalanceModal booking={booking} onClose={() => setOpen(false)} onDone={() => { setOpen(false); onChanged?.(); }}/>}
    </div>
  );
}

export function ChargeBalanceModal({ booking, onClose, onDone }) {
  const [info, setInfo] = useState(null);
  const [reason, setReason] = useState("");
  const [custom, setCustom] = useState("");
  const [cardId, setCardId] = useState("");
  const [method, setMethod] = useState("card");
  const [reference, setReference] = useState("");
  const [sendSms, setSendSms] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/payments/balance/${booking.booking_id}`).then(r => {
      setInfo(r.data);
      if (r.data.cards?.length) setCardId(r.data.cards[0].saved_card_id);
      else setMethod("zelle");
    }).catch(e => toast.error(e?.response?.data?.detail || "Could not load balance"));
  }, [booking.booking_id]);

  const finalReason = reason === "__custom" ? custom.trim() : reason;
  const due = info?.balance_due_cents ?? booking.balance_due_cents ?? 0;
  const totalC = info?.total_cents ?? Math.round((booking.fare || 0) * 100);
  // Price went up after payment → "supplemental charge"; otherwise it's the unpaid remainder
  const isSupplemental = booking.payment_status === "balance_due" && booking.original_paid_cents != null;
  const card = info?.cards?.find(c => c.saved_card_id === cardId);

  const submit = async () => {
    if (!finalReason) { toast.error("Select or enter a charge reason"); return; }
    if (method === "card" && !cardId) { toast.error("Select a card on file"); return; }
    const what = method === "card" ? `charge ${money(due)} to ${card?.brand || "card"} ••••${card?.last4}` : `record ${money(due)} received via ${method}`;
    if (!window.confirm(`${what} for "${finalReason}"?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/payments/charge-balance", {
        booking_id: booking.booking_id, reason: finalReason, saved_card_id: method === "card" ? cardId : null,
        method, reference: reference || null, send_sms: sendSms,
      });
      toast.success(`${money(r.data.amount_cents)} collected — ${r.data.fully_paid ? "reservation fully PAID" : "balance remains"}${r.data.sms === "sent" ? " · client texted" : ""}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Charge failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4" data-testid="charge-balance-modal" onClick={onClose}>
      <div className="w-full max-w-md bg-[#0A0A0A] border border-[#D4AF37]/40 p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">{isSupplemental ? "Supplemental charge" : "Charge remaining balance"} · {booking.confirmation_code}</div>
          <div className="font-serif text-2xl mt-1">Collect <span className="text-amber-300" data-testid="cb-amount">{money(due)}</span></div>
          <div className="text-[11px] text-[#71717A] mt-1">{isSupplemental ? "Originally paid" : "Paid so far"} {money(info?.paid_toward_fare_cents ?? booking.paid_toward_fare_cents ?? 0)} · {isSupplemental ? "new total" : "total"} {money(totalC)}</div>
        </div>

        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Charge reason</span>
          <select data-testid="cb-reason" value={reason} onChange={e => setReason(e.target.value)}
            className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm text-[#F5F2EB]">
            <option value="">Select a reason…</option>
            {(info?.reasons || []).map(r => <option key={r} value={r}>{r}</option>)}
            <option value="__custom">Custom reason…</option>
          </select>
        </label>
        {reason === "__custom" && (
          <input data-testid="cb-reason-custom" value={custom} onChange={e => setCustom(e.target.value)} maxLength={80} autoFocus
            placeholder="e.g. Added second drop-off in Waukesha"
            className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-[#F5F2EB]"/>
        )}

        <div>
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Collect via</span>
          <div className="mt-1 flex gap-1 flex-wrap">
            {[["card", "Card on file"], ["zelle", "Zelle"], ["venmo", "Venmo"], ["cashapp", "Cash App"], ["cash", "Cash"], ["check", "Check"]].map(([m, l]) => (
              <button key={m} type="button" data-testid={`cb-method-${m}`} onClick={() => setMethod(m)}
                disabled={m === "card" && !(info?.cards?.length)}
                className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] border disabled:opacity-30 ${method === m ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/15 text-[#A1A1AA] hover:border-white/40"}`}>{l}</button>
            ))}
          </div>
        </div>

        {method === "card" ? (
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Card on file</span>
            <select data-testid="cb-card" value={cardId} onChange={e => setCardId(e.target.value)}
              className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm text-[#F5F2EB]">
              {(info?.cards || []).map(c => (
                <option key={c.saved_card_id} value={c.saved_card_id}>
                  {c.brand} •••• {c.last4} · {c.cardholder_name || "—"} · exp {c.exp_month}/{c.exp_year}{c.saved_card_id === booking.paid_via_saved_card ? " · used for original payment" : ""}
                </option>
              ))}
            </select>
            {!(info?.cards?.length) && <div className="text-[11px] text-amber-300 mt-1" data-testid="cb-no-card">No card on file for this client — add one in Payments, or record the balance via Zelle / cash.</div>}
          </label>
        ) : (
          <input data-testid="cb-reference" value={reference} onChange={e => setReference(e.target.value)} placeholder="Reference / confirmation # (optional)"
            className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-[#F5F2EB]"/>
        )}

        <label className="flex items-center gap-2 text-xs text-[#A1A1AA] cursor-pointer">
          <input type="checkbox" data-testid="cb-sms" checked={sendSms} onChange={e => setSendSms(e.target.checked)} className="accent-[#D4AF37]"/>
          Text the client a confirmation with the reason
        </label>
        {sendSms && finalReason && (
          <div className="text-[11px] text-[#71717A] border border-white/10 bg-black/40 p-2 leading-snug" data-testid="cb-sms-preview">
            {isSupplemental
              ? `“Delux Limo: Your reservation ${booking.confirmation_code} was updated. A supplemental charge of ${money(due)} for ${finalReason} has been processed${card && method === "card" ? ` to your card ending ${card.last4}` : ""}. Your new total of ${money(totalC)} is fully paid. Thank you!”`
              : `“Delux Limo: A payment of ${money(due)} (${finalReason}) for reservation ${booking.confirmation_code} has been processed${card && method === "card" ? ` to your card ending ${card.last4}` : ""}. Your total of ${money(totalC)} is now fully paid. Thank you!”`}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button data-testid="cb-cancel" onClick={onClose} className="btn-ghost !py-1.5 !px-3 text-[10px]">Cancel</button>
          <button data-testid="cb-submit" onClick={submit} disabled={busy || !info} className="btn-gold !py-1.5 !px-4 text-[10px] inline-flex items-center gap-1.5">
            <CreditCard size={12}/> {busy ? "Processing…" : (method === "card" ? `Charge ${money(due)}` : `Record ${money(due)}`)}
          </button>
        </div>
      </div>
    </div>
  );
}
